import { useState } from 'react'
import Head from 'next/head'
import { sanityClient } from '@bww/beke-lib/sanity-client'
import Hero from '@bww/beke-ui/Project/Hero'
import Details from '@bww/beke-ui/Project/Details'
import BrandSpotlight from '@bww/beke-ui/Project/BrandSpotlight'
import YourDreamCTA from '@bww/beke-ui/YourDreamCTA'

import ImageViewer from '@bww/beke-ui/ImageViewer'

export default function ProjectPage({ project }) {
  const otherImages = [...project.images]
  const heroImage = otherImages.shift()
  const [imageViewerVisible, setImageViewerVisible] = useState(false)

  const imageCredits = Array.from(
    new Set(
      project.images
        .map((image) => image.credit)
        .filter((credit) => typeof credit === 'string'),
    ),
  )

  const pageTitleSuffix = [
    '-',
    'bathroom project',
    project.locationName ? `in ${project.locationName}` : '',
  ]
    .join(' ')
    .trim()

  return (
    <>
      <Head>
        <title>{`${project.title} ${pageTitleSuffix}`}</title>
        <meta name="description" content={project.shortDescription} />
      </Head>
      <Hero
        title={project.title}
        shortDescription={project.shortDescription}
        heroImage={heroImage}
        numOtherImages={otherImages.length}
        featuredComment={project.featuredComment}
        locationName={project.locationName}
        clientTestimonial={project.clientTestimonial}
        showImageViewer={() => {
          setImageViewerVisible(true)
        }}
      />
      <Details
        images={otherImages}
        designOverview={project.designOverview}
        highlights={project.highlights}
        imageCredits={imageCredits.length > 0 ? imageCredits : undefined}
      />
      <ImageViewer
        images={project.images}
        visible={imageViewerVisible}
        close={() => {
          setImageViewerVisible(false)
        }}
      />
      {project.brands && <BrandSpotlight brands={project.brands} />}
      <YourDreamCTA />
    </>
  )
}

export const getStaticProps = async (context) => {
  const project = await sanityClient.fetch(
    `*[_type == "project" && slug.current == $slug][0]{
        title,
        slug,
        images[]{
          ...,
          "lqip": asset->metadata.lqip,
          "aspectRatio": asset->metadata.dimensions.aspectRatio
        },
        shortDescription,
        featuredComment,
        clientTestimonial,
        designOverview,
        highlights,
        locationName,
        brands[]->{
          name,
          "slug": slug.current,
          logo{ "src": asset->url, "bleedColor": bleedColor.hex }
        },
        tags
      }`,
    { slug: context.params.slug },
  )

  if (!project)
    return { redirect: { destination: '/projects', permanent: false } }

  return {
    props: { project },
  }
}

export async function getStaticPaths() {
  const projects = await sanityClient.fetch(
    `*[_type == "project" && company == "be"]{ "slug": slug.current }`,
  )

  return {
    paths: projects.map(({ slug }) => ({
      params: { slug },
    })),
    fallback: false,
  }
}
